import React, { useEffect, useState } from "react";
import {
  deleteAllDevices,
  deleteDevice,
  fetchMyDevices,
  getTrustedDeviceToken,
  isCurrentDeviceTrusted,
  updateDeviceActivity,
} from "../../../api/userDevices";
import logger from "../../../utils/logger";
import {
  Smartphone,
  RefreshCw,
  Trash2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import "./DevicesTab.css";

function formatDate(value, isAr) {
  if (!value) return isAr ? "غير متوفر" : "Not available";
  try {
    return new Date(value).toLocaleString(isAr ? "ar" : "en");
  } catch {
    return String(value);
  }
}

function getDeviceKindLabel(device, isAr) {
  const type = String(device?.device_type || "").toLowerCase();

  if (type === "mobile") return isAr ? "هاتف" : "Phone";
  if (type === "tablet") return isAr ? "جهاز لوحي" : "Tablet";
  if (type === "web") return isAr ? "كمبيوتر / متصفح" : "Desktop / Browser";
  return isAr ? "جهاز" : "Device";
}

function getDeviceDisplayName(device, isAr) {
  const explicitName = String(device?.device_name || "").trim();
  if (explicitName) return explicitName;
  return getDeviceKindLabel(device, isAr);
}

function getDeviceIdentifier(device) {
  const token = String(device?.fcm_token || "").trim();
  if (!token) return "-";
  return token.length <= 18 ? token : token.slice(-12).toUpperCase();
}

export default function DevicesTab({ isAr = false }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const trustedToken = getTrustedDeviceToken();

  async function loadDevices() {
    try {
      setLoading(true);
      setError("");
      const nextDevices = await fetchMyDevices();
      setDevices(Array.isArray(nextDevices) ? nextDevices : []);
    } catch (loadError) {
      logger.error("Devices load error", loadError);
      setError(isAr ? "فشل تحميل الأجهزة." : "Failed to load devices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDevices();
  }, [isAr]);

  async function handleDelete(deviceId) {
    try {
      setBusyId(deviceId);
      setMessage("");
      setError("");
      await deleteDevice(deviceId);
      setMessage(isAr ? "تم حذف الجهاز." : "Device deleted.");
      await loadDevices();
    } catch (deleteError) {
      logger.error("Device delete error", deleteError);
      setError(isAr ? "تعذر حذف الجهاز." : "Could not delete the device.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeleteAll() {
    try {
      setBusyId("all");
      setMessage("");
      setError("");
      await deleteAllDevices();
      setMessage(isAr ? "تم حذف جميع الأجهزة." : "All devices deleted.");
      await loadDevices();
    } catch (deleteError) {
      logger.error("Delete all devices error", deleteError);
      setError(isAr ? "تعذر حذف الأجهزة." : "Could not delete devices.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleRefreshActivity(deviceId) {
    try {
      setBusyId(deviceId);
      setMessage("");
      setError("");
      await updateDeviceActivity(deviceId);
      setMessage(isAr ? "تم تحديث النشاط." : "Activity updated.");
      await loadDevices();
    } catch (updateError) {
      logger.error("Device activity update error", updateError);
      setError(isAr ? "تعذر تحديث النشاط." : "Could not update activity.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="devices-container" dir={isAr ? "rtl" : "ltr"}>
      <div className="devices-header">
        <div className="devices-header-left">
          <div className="devices-header-icon">
            <Smartphone size={20} strokeWidth={2} />
          </div>
          <h3 className="devices-header-title">
            {isAr ? "الأجهزة الموثوقة" : "Trusted Devices"}
          </h3>
        </div>

        <button
          type="button"
          className="devices-delete-all-btn"
          onClick={handleDeleteAll}
          disabled={busyId === "all" || devices.length === 0}
        >
          <Trash2 size={14} />
          <span>
            {busyId === "all"
              ? isAr
                ? "جارٍ الحذف..."
                : "Deleting..."
              : isAr
                ? "حذف الكل"
                : "Delete All"}
          </span>
        </button>
      </div>

      {message && (
        <div className="devices-message success">
          <CheckCircle size={18} />
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="devices-message error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <p className="devices-description">
        {isAr
          ? "تظهر هنا فقط الأجهزة التي وافقت على توثيقها عند تسجيل الدخول."
          : "Only devices you approved as trusted during login appear here."}
      </p>

      {loading ? (
        <div className="devices-loading">
          <div className="devices-loading-spinner" />
          <p className="devices-loading-text">
            {isAr ? "جارٍ التحميل..." : "Loading..."}
          </p>
        </div>
      ) : devices.length === 0 ? (
        <div className="devices-empty">
          <div className="devices-empty-icon">
            <Smartphone size={32} strokeWidth={1.5} />
          </div>
          <p className="devices-empty-text">
            {isAr ? "لا توجد أجهزة محفوظة." : "No saved devices."}
          </p>
        </div>
      ) : (
        <div className="devices-grid">
          {(() => {
            const seen = new Set();
            const uniqueDevices = [];

            for (const device of devices) {
              const deviceId = String(device?.mac_address || device?.fcm_token || device?.id || "");
              if (!seen.has(deviceId)) {
                seen.add(deviceId);
                uniqueDevices.push(device);
              }
            }

            return uniqueDevices.map((device, idx) => (
            <div className="devices-card" key={`${device.mac_address || device.fcm_token || device.id}-${idx}`}>
              <div className="devices-card-icon">
                <Smartphone size={22} strokeWidth={2} />
              </div>

              <h4 className="devices-card-name">
                {getDeviceDisplayName(device, isAr)}
              </h4>

              <div className="devices-card-meta">
                <div className="devices-card-row">
                  <span className="devices-card-label">
                    {isAr ? "النوع:" : "Type:"}
                  </span>
                  <span className="devices-card-value">
                    {getDeviceKindLabel(device, isAr)}
                  </span>
                </div>

                <div className="devices-card-row">
                  <span className="devices-card-label">
                    {isAr ? "آخر نشاط:" : "Last active:"}
                  </span>
                  <span className="devices-card-value">
                    {formatDate(device.last_active, isAr)}
                  </span>
                </div>

                <div className="devices-card-row">
                  <span className="devices-card-label">
                    {isAr ? "معرّف الجهاز:" : "Device ID:"}
                  </span>
                  <span className="devices-card-value">
                    {getDeviceIdentifier(device)}
                  </span>
                </div>
              </div>

              {trustedToken && isCurrentDeviceTrusted(device) && (
                <div className="devices-current-badge">
                  <CheckCircle size={12} />
                  <span>{isAr ? "الجهاز الحالي" : "This device"}</span>
                </div>
              )}

              <div className="devices-actions">
                <button
                  type="button"
                  className="devices-btn refresh"
                  onClick={() => handleRefreshActivity(device.id)}
                  disabled={busyId === device.id}
                  title={isAr ? "تحديث النشاط" : "Refresh Activity"}
                >
                  <RefreshCw size={14} />
                  <span>{isAr ? "تحديث" : "Refresh"}</span>
                </button>

                <button
                  type="button"
                  className="devices-btn delete"
                  onClick={() => handleDelete(device.id)}
                  disabled={busyId === device.id}
                  title={isAr ? "حذف الجهاز" : "Delete Device"}
                >
                  <Trash2 size={14} />
                  <span>
                    {busyId === device.id
                      ? isAr
                        ? "جارٍ..."
                        : "Working..."
                      : isAr
                        ? "حذف"
                        : "Delete"}
                  </span>
                </button>
              </div>
            </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
}
